import { useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import {
  IconChevronLeft,
  IconLock,
  IconLockOpen,
  IconPlus,
  IconTrash,
} from "@tabler/icons-react";
import { Button } from "@/components/ui/button";
import { useBoat } from "@/api/hooks/use-boats";
import { usePlatformWeighings } from "@/api/hooks/use-platform-weighings";
import { useWingWeighings } from "@/api/hooks/use-wing-weighings";
import { usePlatformWeighingActions } from "@/hooks/use-platform-weighing-actions";
import { useWingWeighingActions } from "@/hooks/use-wing-weighing-actions";
import { WeighingStateBadge } from "@/components/weighing-state-badge";
import { GateBadge } from "@/components/gate-badge";
import { asBoatId, type PlatformWeighingId, type WingWeighingId } from "@/types/identifiers";
import type { components } from "@/api/generated/schema";

type PlatformWeighing = components["schemas"]["PlatformWeighingResponse-Output"];
type WingWeighing = components["schemas"]["WingWeighingResponse"];

type Tab = "platform" | "wing";

function rowBorder(state: string): string {
  return state === "signed_off"
    ? "border-l-emerald-500"
    : state === "draft"
    ? "border-l-blue-400"
    : "border-l-border";
}

// Rows navigate to the weighing page (edit for drafts, read-only otherwise);
// the state-changing actions stay inline with stopPropagation.
function PlatformRow({
  w,
  onOpen,
  onSignOff,
  onUnlock,
  onDelete,
}: {
  w: PlatformWeighing;
  onOpen: (w: PlatformWeighing) => void;
  onSignOff: (id: PlatformWeighingId) => void;
  onUnlock: (id: PlatformWeighingId) => void;
  onDelete: (id: PlatformWeighingId) => void;
}) {
  const grossW = useMemo(() => {
    const active = w.measurements.filter((m) => !m.excluded);
    if (active.length === 0) return null;
    const sums = active.map(
      (m) => m.stbd_aft_kg + m.port_aft_kg + m.stbd_fwd_kg + m.port_fwd_kg,
    );
    return sums.reduce((a, b) => a + b, 0) / sums.length;
  }, [w.measurements]);

  return (
    <div
      onClick={() => onOpen(w)}
      className={`border-l-3 ${rowBorder(w.state)} bg-card rounded-r-lg pl-4 pr-5 py-3 cursor-pointer transition-shadow hover:shadow-[0_1px_4px_rgba(0,0,0,0.04)]`}
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
        <div className="min-w-0 sm:flex-1">
          <div className="flex items-center gap-2 mb-0.5 flex-wrap">
            <WeighingStateBadge state={w.state} />
            {w.state === "signed_off" && w.gate_ok === false && <GateBadge />}
            <span className="text-xs text-muted-foreground">
              {new Date(w.measured_at).toLocaleDateString()}
            </span>
            <span className="text-xs text-muted-foreground">by {w.measured_by}</span>
          </div>
          <div className="text-sm text-foreground tabular-nums">
            <span className="font-semibold">{grossW ? `${grossW.toFixed(1)} kg gross` : "—"}</span>
            <span className="text-muted-foreground mx-2">|</span>
            <span>Oil {w.oil_kg.toFixed(1)} kg @ {w.oil_lcg.toFixed(2)} m</span>
            <span className="text-muted-foreground mx-2">|</span>
            <span>{w.measurements.filter((m) => !m.excluded).length}/{w.measurements.length} sets</span>
          </div>
          {w.notes && (
            <div className="text-xs text-muted-foreground mt-1 truncate">{w.notes}</div>
          )}
        </div>
        <div className="flex items-center gap-1.5 shrink-0 flex-wrap">
          {w.state === "draft" && (
            <>
              <Button
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  onSignOff(w.id as PlatformWeighingId);
                }}
              >
                Sign off
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={(e) => {
                  e.stopPropagation();
                  if (window.confirm("Delete this draft weighing?")) {
                    onDelete(w.id as PlatformWeighingId);
                  }
                }}
              >
                <IconTrash className="w-4 h-4" />
              </Button>
            </>
          )}
          {w.state === "signed_off" && (
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                if (window.confirm("Unlock for editing? This will move it back to draft.")) {
                  onUnlock(w.id as PlatformWeighingId);
                }
              }}
            >
              <IconLockOpen className="w-4 h-4 mr-1" /> Unlock
            </Button>
          )}
          {w.state === "superseded" && (
            <span className="text-xs text-muted-foreground italic flex items-center gap-1">
              <IconLock className="w-3 h-3" /> historic
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function WingRow({
  w,
  onOpen,
  onSignOff,
  onUnlock,
  onDelete,
}: {
  w: WingWeighing;
  onOpen: (w: WingWeighing) => void;
  onSignOff: (id: WingWeighingId) => void;
  onUnlock: (id: WingWeighingId) => void;
  onDelete: (id: WingWeighingId) => void;
}) {
  const grossW = useMemo(() => {
    const active = w.measurements.filter((m) => !m.excluded);
    if (active.length === 0) return null;
    const sums = active.map((m) => m.head_kg + m.clew_kg + m.butt_kg);
    return sums.reduce((a, b) => a + b, 0) / sums.length;
  }, [w.measurements]);

  return (
    <div
      onClick={() => onOpen(w)}
      className={`border-l-3 ${rowBorder(w.state)} bg-card rounded-r-lg pl-4 pr-5 py-3 cursor-pointer transition-shadow hover:shadow-[0_1px_4px_rgba(0,0,0,0.04)]`}
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
        <div className="min-w-0 sm:flex-1">
          <div className="flex items-center gap-2 mb-0.5">
            <WeighingStateBadge state={w.state} />
            <span className="text-[10px] font-semibold tracking-wider uppercase bg-muted px-2 py-0.5 rounded">
              {w.config}
            </span>
            <span className="text-xs text-muted-foreground">
              {new Date(w.measured_at).toLocaleDateString()}
            </span>
            <span className="text-xs text-muted-foreground">by {w.measured_by}</span>
          </div>
          <div className="text-sm text-foreground tabular-nums">
            <span className="font-semibold">{grossW ? `${grossW.toFixed(1)} kg gross` : "—"}</span>
            <span className="text-muted-foreground mx-2">|</span>
            <span className="text-xs">
              {w.mhu_enabled ? "MHU " : ""}
              {w.dicks_enabled ? "Dicks " : ""}
              {w.screen_enabled ? "Screen " : ""}
            </span>
            <span className="text-muted-foreground mx-2">|</span>
            <span>{w.measurements.filter((m) => !m.excluded).length}/{w.measurements.length} sets</span>
          </div>
          {w.notes && <div className="text-xs text-muted-foreground mt-1 truncate">{w.notes}</div>}
        </div>
        <div className="flex items-center gap-1.5 shrink-0 flex-wrap">
          {w.state === "draft" && (
            <>
              <Button
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  onSignOff(w.id as WingWeighingId);
                }}
              >
                Sign off
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={(e) => {
                  e.stopPropagation();
                  if (window.confirm("Delete this draft weighing?")) {
                    onDelete(w.id as WingWeighingId);
                  }
                }}
              >
                <IconTrash className="w-4 h-4" />
              </Button>
            </>
          )}
          {w.state === "signed_off" && (
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                if (window.confirm("Unlock for editing? This will move it back to draft.")) {
                  onUnlock(w.id as WingWeighingId);
                }
              }}
            >
              <IconLockOpen className="w-4 h-4 mr-1" /> Unlock
            </Button>
          )}
          {w.state === "superseded" && (
            <span className="text-xs text-muted-foreground italic flex items-center gap-1">
              <IconLock className="w-3 h-3" /> historic
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

export function BoatWeighingsPage() {
  const params = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const boatId = asBoatId(params.boatId ?? "");

  const boatQuery = useBoat(boatId);
  const platformQuery = usePlatformWeighings(boatId);
  const wingQuery = useWingWeighings(boatId);

  const platformActions = usePlatformWeighingActions(boatId, platformQuery.data);
  const wingActions = useWingWeighingActions(boatId, wingQuery.data);

  // ?tab=platform|wing deep-links from the fleet weighings index
  const initialTab: Tab = searchParams.get("tab") === "wing" ? "wing" : "platform";
  const [tab, setTab] = useState<Tab>(initialTab);

  const boat = boatQuery.data;
  const boatCode = boat?.boat_code ?? "—";
  const boatNumber = boat?.boat_number ?? "";

  return (
    <div>
      {/* Header */}
      <div className="mb-4">
        {/* Deterministic like every other back affordance — a blind
            history.back() ping-pongs with the weighing pages' back-links. */}
        <Link
          to="/admin/weighings"
          className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 mb-2 w-fit"
        >
          <IconChevronLeft className="w-3 h-3" /> Fleet weighings
        </Link>
        <div className="flex items-baseline gap-3">
          <h1 className="text-2xl font-bold">{boatCode}</h1>
          <span className="text-sm text-muted-foreground">{boatNumber}</span>
        </div>
        <p className="text-sm text-muted-foreground">Boat-level weighings (cross-event)</p>
      </div>

      {/* Tab switcher */}
      <div className="flex gap-1 bg-muted rounded-lg p-1 mb-4 w-fit">
        {(["platform", "wing"] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-1.5 rounded text-sm font-semibold transition-colors ${
              tab === t ? "bg-card shadow ring-1 ring-border" : "text-muted-foreground"
            }`}
          >
            {t === "platform" ? "Platform" : "Wing"}
          </button>
        ))}
      </div>

      {/* Content per tab */}
      {tab === "platform" && (
        <div className="space-y-3">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div className="text-sm text-muted-foreground">
              {platformActions.weighings.length} weighing
              {platformActions.weighings.length === 1 ? "" : "s"} (
              {platformActions.signedOffCount} signed off)
            </div>
            <Button onClick={() => navigate(`/boats/${boatId}/weighings/platform/new`)} size="sm">
              <IconPlus className="w-4 h-4 mr-1" /> New platform weighing
            </Button>
          </div>
          {platformQuery.isLoading ? (
            <div className="text-sm text-muted-foreground py-8 text-center">Loading…</div>
          ) : platformActions.weighings.length === 0 ? (
            <div className="text-sm text-muted-foreground py-8 text-center border border-dashed border-border rounded-lg">
              No platform weighings yet. Click "New platform weighing" to capture one.
            </div>
          ) : (
            platformActions.weighings.map((w) => (
              <PlatformRow
                key={w.id}
                w={w}
                onOpen={(pw) => navigate(`/boats/${boatId}/weighings/platform/${pw.id}`)}
                onSignOff={platformActions.handleSignOff}
                onUnlock={platformActions.handleUnlock}
                onDelete={platformActions.handleDelete}
              />
            ))
          )}
        </div>
      )}

      {tab === "wing" && (
        <div className="space-y-3">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div className="text-sm text-muted-foreground">
              {wingActions.weighings.length} weighing
              {wingActions.weighings.length === 1 ? "" : "s"} (
              {wingActions.signedOffCount} signed off)
            </div>
            <Button onClick={() => navigate(`/boats/${boatId}/weighings/wing/new`)} size="sm">
              <IconPlus className="w-4 h-4 mr-1" /> New wing weighing
            </Button>
          </div>
          {wingQuery.isLoading ? (
            <div className="text-sm text-muted-foreground py-8 text-center">Loading…</div>
          ) : wingActions.weighings.length === 0 ? (
            <div className="text-sm text-muted-foreground py-8 text-center border border-dashed border-border rounded-lg">
              No wing weighings yet. Click "New wing weighing" to capture one.
            </div>
          ) : (
            wingActions.weighings.map((w) => (
              <WingRow
                key={w.id}
                w={w}
                onOpen={(ww) => navigate(`/boats/${boatId}/weighings/wing/${ww.id}`)}
                onSignOff={wingActions.handleSignOff}
                onUnlock={wingActions.handleUnlock}
                onDelete={wingActions.handleDelete}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
}
